<script setup lang="ts">
import type { EventVault } from "@/models"
import type { IGuestMessage } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, toRef } from "vue"
import { z } from "zod"
import { useAddGuestMessage } from "@/api"
import { Button, Drawer, Icon, Input, Textarea } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import Uploader from "@/components/Uploader/Uploader.vue"
import { useModal, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { GuestbookMessageDrawerKey } from "@/symbols"

export type GuestbookMessageDrawerMode = "text" | "video"

const props = defineProps<{
  mode: GuestbookMessageDrawerMode
  eventVault?: EventVault
  albumId?: number
  isFeatureDisabled?: boolean
  isEventVaultLoading?: boolean
}>()

const { t } = useTranslation()

const toast = useToast()

const { toggle: toggleGuestbookMessageDrawer } = useModal(GuestbookMessageDrawerKey)

const eventVaultRef = toRef(props, "eventVault")

const validationErrors = ref<ValidationException<unknown> | null>(null)

const queryClient = useQueryClient()

function optionalGuestEmailSchema(emailMessage: string) {
  return z.preprocess(
    val => (val ?? ""),
    z.union([z.literal(""), z.string().email(emailMessage)]),
  )
}

const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      fromName: z.string().optional(),
      fromEmail: eventVaultRef.value?.eventVaultSettings?.guestEmailRequired
        ? z.string().email(t("add_guest_message_page.error.email"))
        : optionalGuestEmailSchema(t("add_guest_message_page.error.email")),
      message: z
        .string()
        .min(1, t("add_guest_message_page.error.note_min"))
        .max(5000, t("add_guest_message_page.error.note_max")),
    }),
  ),
)

const { handleSubmit, handleReset } = useForm({
  validationSchema,
  initialValues: {
    fromName: "",
    fromEmail: "",
    message: "",
  },
})

const { mutate: mutateAddGuestMessage, isPending: isAddMessagePending } = useAddGuestMessage()

const drawerTitle = computed(() => props.mode === "text" ? t("guestbook_message_drawer.text_title") : t("guestbook_message_drawer.video_title"))

const onSubmit = handleSubmit(({ fromName, fromEmail, message }) => {
  if (!props.eventVault?.id) {
    toast.error({ description: t("add_guest_message_page.missing_id") })
    return
  }

  validationErrors.value = null

  const guestMessage: IGuestMessage = {
    fromName,
    fromEmail,
    message,
    eventAlbumID: props.albumId,
  }

  mutateAddGuestMessage({ message: guestMessage, eventVaultId: props.eventVault?.id }, {
    onSuccess: async (responseMessage: string) => {
      const successMessage = responseMessage || t("add_guest_message_page.success")
      handleReset()
      await queryClient.invalidateQueries({ queryKey: ["listMessages"] })
      toggleGuestbookMessageDrawer()
      toast.success({ description: successMessage })
    },
    onError: (error: unknown) => {
      let errorMessage = t("try_again")

      if (error instanceof ValidationException) {
        errorMessage = t("validation_error")
        validationErrors.value = error
      }

      toast.error({ description: errorMessage })
    },
  })
})
</script>

<template>
  <Drawer :name="GuestbookMessageDrawerKey" :title="drawerTitle">
    <template v-if="mode === 'video'">
      <div data-test="guestbook-drawer-video-panel">
        <p class="mb-8 font-montserrat text-gray-600">
          {{ t("guestbook_message_drawer.video_description") }}
        </p>
        <Uploader
          auto-proceed
          can-upload-videos
          :additional-file-meta="{ eventVidType: 2 }"
          :direct-uploads="eventVault?.directUpload"
        />
      </div>
    </template>

    <template v-else>
      <div class="guest-layout" data-test="guestbook-drawer-text-panel">
        <p class="mb-6 font-montserrat text-gray-600">
          {{ t("add_guest_message_page.message") }}
        </p>

        <form @submit.prevent="onSubmit">
          <div class="flex flex-col gap-4">
            <Input
              name="fromName"
              :label="t('add_guest_message_page.form.name')"
              :hint="t('add_guest_message_page.form.name_hint')"
              :disabled="isFeatureDisabled || Boolean(isAddMessagePending)"
            />
            <Input
              name="fromEmail"
              :label="t('add_guest_message_page.form.email')"
              :error="validationErrors?.firstValidationError('Email') ?? undefined"
              :disabled="isFeatureDisabled || Boolean(isAddMessagePending)"
            />
            <Textarea
              name="message"
              :label="t('add_guest_message_page.form.note')"
              :disabled="isFeatureDisabled || Boolean(isAddMessagePending)"
            />
            <Button
              data-test="add-message-submit"
              class="w-full"
              :disabled="isFeatureDisabled || Boolean(isAddMessagePending)"
            >
              {{ isAddMessagePending ? "Submitting..." : t("add_guest_message_page.form.leave_note_button") }}
              <template #suffixIcon>
                <Icon v-if="isAddMessagePending" icon="Loading" />
              </template>
            </Button>
          </div>
        </form>
      </div>
    </template>
  </Drawer>
</template>
